import { Controller } from "@hotwired/stimulus"

// To / Cc / Bcc as Gmail-style chips. The hidden field is the value send and
// drafts already know (comma-separated); the visible input is only for the
// token being typed. Markup for a chip or a suggestion is cloned from a
// server template — this controller never builds either.
//
// Space commits here even though ReplyRecipients does not split on whitespace:
// "Jane Doe <jane@x.com>" is one pasted token (comma/semicolon only). A typed
// name that matches a project contact becomes a chip via the suggestions list.

const SEPARATOR = /[,;]+/
const DELIVERABLE = /^[^@\s]+@[^@\s]+$/
const UNDELIVERABLE = [ "unknown-sender@invalid.local" ]
const COMMIT_KEYS = new Set([ " ", ",", ";", "Enter" ])
const DEBOUNCE_MS = 200
const VIEWPORT_MARGIN = 8
const TRIGGER_GAP = 6

export default class extends Controller {
  static targets = [ "input", "field", "chip", "chipTemplate", "menu", "list", "empty", "optionTemplate" ]
  static values = { suggestionsUrl: String }

  connect() {
    // Composer may have already written a restored draft onto the hidden field
    // before this connected. Read it now so we do not keep the server-rendered
    // defaults over that stash.
    this.syncFromField()
    this.closeOnScroll = this.closeOnScroll.bind(this)
  }

  disconnect() {
    this.clearDebounce()
    this.abortSuggestions()
    this.removeSuggestionListeners()
  }

  keydown(event) {
    if (event.isComposing || event.keyCode === 229) return

    if (this.suggestionsOpen) {
      if (event.key === "ArrowDown") {
        event.preventDefault()
        this.moveHighlight(1)
        return
      }
      if (event.key === "ArrowUp") {
        event.preventDefault()
        this.moveHighlight(-1)
        return
      }
      if (event.key === "Enter") {
        event.preventDefault()
        if (this.highlighted) this.pickHighlighted()
        else if (this.inputTarget.value.trim()) this.commitToken(this.inputTarget.value)
        return
      }
      if (event.key === "Escape") {
        event.preventDefault()
        this.closeSuggestions()
        return
      }
    }

    if (COMMIT_KEYS.has(event.key)) {
      event.preventDefault()
      this.closeSuggestions()
      if (this.inputTarget.value.trim()) this.commitToken(this.inputTarget.value)
      return
    }

    if ((event.key === "Backspace" || event.key === "Delete") && this.inputIsEmpty()) {
      event.preventDefault()
      this.closeSuggestions()
      this.removeLastChip()
      return
    }

    if (event.key === "ArrowLeft" && this.caretAtStart()) {
      event.preventDefault()
      this.closeSuggestions()
      this.activateChip(this.lastChip)
    }
  }

  input() {
    const value = this.inputTarget.value
    const committed = value.match(/^(.*?)([,;]|\s)$/)
    if (committed && committed[1].trim()) {
      this.closeSuggestions()
      this.commitToken(committed[1])
      return
    }

    this.writeField()
    this.scheduleSuggestions()
  }

  paste(event) {
    const text = event.clipboardData?.getData("text/plain")
    if (!text || !SEPARATOR.test(text)) return

    event.preventDefault()
    this.closeSuggestions()
    text.split(SEPARATOR).forEach((piece) => this.commitToken(piece))
  }

  blur() {
    this.closeSuggestions()
    this.commitToken(this.inputTarget.value, { requireValid: true })
  }

  commitPending() {
    this.commitToken(this.inputTarget.value)
  }

  focusInput(event) {
    if (event.target.closest("[data-recipient-chips-target='chip']")) return
    if (event.target.closest("[data-recipient-chips-target='menu']")) return

    this.inputTarget.focus()
    this.clearSelection()
  }

  selectChip(event) {
    this.activateChip(event.currentTarget)
  }

  chipKeydown(event) {
    const chip = event.currentTarget

    if (event.key === "ArrowLeft") {
      event.preventDefault()
      this.activateChip(this.previousChip(chip) || chip)
      return
    }
    if (event.key === "ArrowRight") {
      event.preventDefault()
      const next = this.nextChip(chip)
      if (next) this.activateChip(next)
      else this.focusTyping()
      return
    }
    if (event.key === "Backspace" || event.key === "Delete") {
      event.preventDefault()
      this.removeChipElement(chip)
      return
    }
    if (event.key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey) {
      event.preventDefault()
      this.focusTyping()
      this.inputTarget.value += event.key
      this.writeField()
      this.scheduleSuggestions()
    }
  }

  removeChip(event) {
    this.removeChipElement(event.currentTarget.closest("[data-recipient-chips-target='chip']"))
  }

  holdPosition(event) {
    event.preventDefault()
  }

  pickSuggestion(event) {
    this.commitSuggestion(event.currentTarget)
  }

  syncFromField() {
    if (this.writing) return

    this.replaceChips(this.constructor.tokensFrom(this.fieldTarget.value))
    this.inputTarget.value = ""
    this.syncValidity()
  }

  get hasInvalidChips() {
    return this.chipTargets.some((chip) => !this.chipValid(chip))
  }

  focusInvalid() {
    const chip = this.chipTargets.find((candidate) => !this.chipValid(candidate))
    if (chip) this.activateChip(chip)
    else this.focusTyping()
  }

  commitToken(raw, { requireValid = false } = {}) {
    const token = this.constructor.parseToken(raw)
    if (!token) {
      if (!String(raw ?? "").trim()) this.inputTarget.value = ""
      this.writeField()
      return false
    }
    if (requireValid && !token.valid) return false
    if (!this.hasAddress(token.value)) this.appendChip(token)
    this.inputTarget.value = ""
    this.writeField()
    this.closeSuggestions()
    return true
  }

  writeField() {
    const values = this.chipTargets.map((chip) => chip.dataset.value).filter(Boolean)
    const pending = this.inputTarget.value.trim()
    const next = [ ...values, pending ].filter(Boolean).join(", ")
    if (this.fieldTarget.value === next) {
      this.syncValidity()
      return
    }

    this.writing = true
    this.fieldTarget.value = next
    this.fieldTarget.dispatchEvent(new Event("input", { bubbles: true }))
    this.writing = false
    this.syncValidity()
  }

  replaceChips(tokens) {
    this.chipTargets.forEach((chip) => chip.remove())
    tokens.forEach((token) => this.appendChip(token))
  }

  appendChip(token) {
    const chip = this.chipTemplateTarget.content.firstElementChild.cloneNode(true)
    this.decorateChip(chip, token)
    this.inputTarget.before(chip)
  }

  decorateChip(chip, token) {
    chip.dataset.value = token.value
    chip.classList.toggle("badge--danger", !token.valid)
    chip.classList.toggle("badge--muted", token.valid)
    const address = chip.querySelector("[data-recipient-chips-address]")
    if (address) address.textContent = token.value
    const remove = chip.querySelector(".badge__remove")
    if (remove) {
      const label = `Remove ${token.value}`
      remove.setAttribute("aria-label", label)
      remove.setAttribute("title", label)
    }
  }

  removeLastChip() {
    this.removeChipElement(this.lastChip)
  }

  removeChipElement(chip) {
    if (!chip) return

    const next = this.nextChip(chip) || this.previousChip(chip)
    chip.remove()
    this.writeField()
    if (next) this.activateChip(next)
    else this.focusTyping()
  }

  activateChip(chip) {
    if (!chip) return

    this.clearSelection()
    chip.classList.add("is-selected")
    chip.focus()
  }

  clearSelection() {
    this.chipTargets.forEach((chip) => chip.classList.remove("is-selected"))
  }

  focusTyping() {
    this.clearSelection()
    this.inputTarget.focus()
  }

  syncValidity() {
    const invalid = this.hasInvalidChips
    this.element.classList.toggle("is-invalid", invalid)
    this.element.setAttribute("aria-invalid", invalid ? "true" : "false")
  }

  hasAddress(value) {
    const key = value.toLowerCase()
    return this.chipTargets.some((chip) => (chip.dataset.value || "").toLowerCase() === key)
  }

  chipValid(chip) {
    return !chip.classList.contains("badge--danger")
  }

  previousChip(chip) {
    const index = this.chipTargets.indexOf(chip)
    return index > 0 ? this.chipTargets[index - 1] : null
  }

  nextChip(chip) {
    const index = this.chipTargets.indexOf(chip)
    return index >= 0 ? this.chipTargets[index + 1] || null : null
  }

  get lastChip() {
    return this.chipTargets[this.chipTargets.length - 1] || null
  }

  inputIsEmpty() {
    return this.inputTarget.value === ""
  }

  caretAtStart() {
    return this.inputTarget.selectionStart === 0 && this.inputTarget.selectionEnd === 0
  }

  scheduleSuggestions() {
    this.clearDebounce()
    const query = this.inputTarget.value.trim()
    if (!query || !this.suggestionsUrlValue) {
      this.closeSuggestions()
      return
    }

    this.debounce = setTimeout(() => this.fetchSuggestions(query), DEBOUNCE_MS)
  }

  fetchSuggestions(query) {
    this.abortSuggestions()
    this.abort = new AbortController()

    const url = new URL(this.suggestionsUrlValue, window.location.origin)
    url.searchParams.set("q", query)

    fetch(url, {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
      signal: this.abort.signal
    }).then((response) => {
      if (!response.ok) throw new Error("recipient suggestions failed")
      return response.json()
    }).then((data) => {
      if (this.inputTarget.value.trim() !== query) return

      this.renderSuggestions(data.suggestions || [])
    }).catch((error) => {
      if (error.name === "AbortError") return

      this.closeSuggestions()
    })
  }

  renderSuggestions(suggestions) {
    const visible = suggestions.filter((suggestion) => !this.hasAddress(suggestion.email))
    this.listTarget.replaceChildren()

    visible.forEach((suggestion, index) => {
      const option = this.optionTemplateTarget.content.firstElementChild.cloneNode(true)
      option.id = `${this.listTarget.id}-option-${index}`
      option.dataset.email = suggestion.email
      const title = suggestion.name || suggestion.email
      option.querySelector("[data-name]").textContent = title
      const email = option.querySelector("[data-email]")
      email.textContent = suggestion.email
      email.hidden = !suggestion.name || suggestion.name === suggestion.email
      const avatar = option.querySelector("[data-avatar-seed]")
      if (avatar && suggestion.avatar_seed) {
        avatar.hidden = false
        avatar.dataset.userAvatarSeedValue = suggestion.avatar_seed
      }
      if (index === 0) {
        option.classList.add("is-active")
        option.setAttribute("aria-selected", "true")
      }
      this.listTarget.appendChild(option)
    })

    this.emptyTarget.hidden = visible.length > 0
    this.openSuggestions()
    this.positionMenu()
    this.syncActivedescendant()
  }

  openSuggestions() {
    if (!this.hasMenuTarget) return

    this.menuTarget.hidden = false
    this.inputTarget.setAttribute("aria-expanded", "true")
    this.ignoreScroll = true
    document.addEventListener("scroll", this.closeOnScroll, true)
    requestAnimationFrame(() => { this.ignoreScroll = false })
  }

  closeSuggestions() {
    this.clearDebounce()
    this.abortSuggestions()
    if (!this.hasMenuTarget || this.menuTarget.hidden) {
      this.clearSuggestionAria()
      return
    }

    this.menuTarget.hidden = true
    this.listTarget.replaceChildren()
    this.emptyTarget.hidden = true
    this.clearSuggestionAria()
    this.removeSuggestionListeners()
  }

  clearSuggestionAria() {
    if (!this.hasInputTarget) return

    this.inputTarget.setAttribute("aria-expanded", "false")
    this.inputTarget.removeAttribute("aria-activedescendant")
  }

  pickHighlighted() {
    if (!this.highlighted) return

    this.commitSuggestion(this.highlighted)
  }

  commitSuggestion(option) {
    const email = option.dataset.email
    if (!email) return

    this.commitToken(email)
    this.focusTyping()
  }

  moveHighlight(delta) {
    const options = this.suggestionOptions
    if (options.length === 0) return

    const current = options.indexOf(this.highlighted)
    const start = current === -1 ? (delta > 0 ? -1 : 0) : current
    const next = (start + delta + options.length) % options.length
    options.forEach((option, index) => {
      const active = index === next
      option.classList.toggle("is-active", active)
      if (active) option.setAttribute("aria-selected", "true")
      else option.removeAttribute("aria-selected")
    })
    options[next].scrollIntoView({ block: "nearest" })
    this.syncActivedescendant()
  }

  syncActivedescendant() {
    if (this.highlighted) this.inputTarget.setAttribute("aria-activedescendant", this.highlighted.id)
    else this.inputTarget.removeAttribute("aria-activedescendant")
  }

  positionMenu() {
    const menu = this.menuTarget
    menu.style.position = "fixed"
    menu.style.right = "auto"

    const anchor = this.element.getBoundingClientRect()
    const panel = menu.getBoundingClientRect()
    const below = anchor.bottom + TRIGGER_GAP
    const top = below + panel.height > window.innerHeight - VIEWPORT_MARGIN
      ? anchor.top - panel.height - TRIGGER_GAP
      : below
    let left = anchor.left
    if (left + panel.width > window.innerWidth - VIEWPORT_MARGIN) {
      left = window.innerWidth - panel.width - VIEWPORT_MARGIN
    }

    menu.style.top = `${Math.max(VIEWPORT_MARGIN, top)}px`
    menu.style.left = `${Math.max(VIEWPORT_MARGIN, left)}px`
    menu.style.minWidth = `${anchor.width}px`
  }

  closeOnScroll(event) {
    if (this.ignoreScroll) return
    if (this.hasMenuTarget && this.menuTarget.contains(event.target)) return

    this.closeSuggestions()
  }

  removeSuggestionListeners() {
    document.removeEventListener("scroll", this.closeOnScroll, true)
  }

  clearDebounce() {
    if (this.debounce) {
      clearTimeout(this.debounce)
      this.debounce = null
    }
  }

  abortSuggestions() {
    this.abort?.abort()
    this.abort = null
  }

  get suggestionsOpen() {
    return this.hasMenuTarget && !this.menuTarget.hidden
  }

  get suggestionOptions() {
    return Array.from(this.listTarget.querySelectorAll(".recipient-chips__suggestion"))
  }

  get highlighted() {
    return this.listTarget.querySelector(".recipient-chips__suggestion.is-active")
  }

  static tokensFrom(value) {
    return String(value ?? "").split(SEPARATOR).map((part) => this.parseToken(part)).filter(Boolean)
  }

  static parseToken(raw) {
    const cleaned = String(raw ?? "").replace(/[\r\n]+/g, " ").trim()
    if (!cleaned) return null

    const angled = cleaned.match(/<([^<>]+)>/)
    const address = (angled ? angled[1] : cleaned).replace(/[\r\n]+/g, " ").trim()
    if (!address) return null

    return { value: address, valid: this.deliverable(address) }
  }

  static deliverable(address) {
    return DELIVERABLE.test(address) && !UNDELIVERABLE.includes(address.toLowerCase())
  }
}
